/* ============================================================
   main-product.js
   Vanilla JS -- no jQuery dependency
   Hooks into existing theme ajax cart via .enj-add-to-cart-btn class
   ============================================================ */

(() => {

  /* ---- Carousel + Thumbnails + Arrows ---- */

  function initCarousel(section) {
    const carousel = section.querySelector('.main-product__carousel');
    if (!carousel) return;

    const media = section.querySelector('.main-product__media');
    const layout = media ? media.dataset.layout : '';
    const slides = Array.from(carousel.querySelectorAll('.main-product__carousel-slide'));
    const thumbnails = Array.from(section.querySelectorAll('.main-product__thumbnail'));
    const dots = Array.from(section.querySelectorAll('.main-product__carousel-dot'));
    const prevBtn = section.querySelector('.main-product__arrow--prev');
    const nextBtn = section.querySelector('.main-product__arrow--next');

    if (slides.length < 2) return;

    let currentIndex = 0;

    function isCarouselLayout() {
      if (window.innerWidth <= 767) return true;
      return layout !== 'stacked' && layout !== '2col';
    }

    function goTo(index) {
      if (index < 0 || index >= slides.length) return;
      currentIndex = index;

      if (isCarouselLayout()) {
        carousel.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
      } else {
        slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      updateActive();
    }

    function updateArrowVisibility() {
      if (prevBtn) prevBtn.hidden = currentIndex === 0;
      if (nextBtn) nextBtn.hidden = currentIndex >= slides.length - 1;
    }

    function updateActive() {
      thumbnails.forEach((t, i) => t.setAttribute('aria-selected', i === currentIndex ? 'true' : 'false'));
      dots.forEach((d, i) => d.setAttribute('aria-current', i === currentIndex ? 'true' : 'false'));
      updateArrowVisibility();
    }

    thumbnails.forEach((thumb, i) => {
      thumb.addEventListener('click', () => goTo(i));
    });

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => goTo(i));
    });

    if (prevBtn) {
      prevBtn.addEventListener('click', () => goTo(currentIndex - 1));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => goTo(currentIndex + 1));
    }

    let scrollTimer;
    carousel.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        if (!isCarouselLayout()) return;
        const slideWidth = carousel.offsetWidth;
        if (slideWidth === 0) return;
        const newIndex = Math.round(carousel.scrollLeft / slideWidth);
        if (newIndex !== currentIndex && newIndex >= 0 && newIndex < slides.length) {
          currentIndex = newIndex;
          updateActive();
        }
      }, 60);
    });

    updateActive();
  }

  /* ---- Thumbnail strip arrows ---- */

  function initThumbArrows(section) {
    const wrapper = section.querySelector('.main-product__thumbnails-wrapper');
    if (!wrapper) return;

    const strip = wrapper.querySelector('.main-product__thumbnails');
    const prevBtn = wrapper.querySelector('.main-product__thumb-arrow--prev');
    const nextBtn = wrapper.querySelector('.main-product__thumb-arrow--next');

    if (!strip || !prevBtn || !nextBtn) return;

    const scrollStep = 200;

    function updateVisibility() {
      const isVertical = getComputedStyle(strip).flexDirection === 'column';
      const scrollPos = isVertical ? strip.scrollTop : strip.scrollLeft;
      const scrollSize = isVertical ? strip.scrollHeight - strip.clientHeight : strip.scrollWidth - strip.clientWidth;

      prevBtn.hidden = scrollPos <= 2;
      nextBtn.hidden = scrollPos >= scrollSize - 2;
    }

    prevBtn.addEventListener('click', () => {
      const isVertical = getComputedStyle(strip).flexDirection === 'column';
      if (isVertical) {
        strip.scrollBy({ top: -scrollStep, behavior: 'smooth' });
      } else {
        strip.scrollBy({ left: -scrollStep, behavior: 'smooth' });
      }
    });

    nextBtn.addEventListener('click', () => {
      const isVertical = getComputedStyle(strip).flexDirection === 'column';
      if (isVertical) {
        strip.scrollBy({ top: scrollStep, behavior: 'smooth' });
      } else {
        strip.scrollBy({ left: scrollStep, behavior: 'smooth' });
      }
    });

    strip.addEventListener('scroll', () => {
      requestAnimationFrame(updateVisibility);
    });

    updateVisibility();

    const resizeObserver = new ResizeObserver(() => updateVisibility());
    resizeObserver.observe(strip);
  }

  /* ---- Variant Picker ---- */

  function initVariantPicker(section) {
    const form = section.querySelector('.main-product__form');
    if (!form) return;

    const productData = JSON.parse(section.querySelector('[data-product-json]')?.textContent || 'null');
    if (!productData) return;

    const variantInput = form.querySelector('[name="id"]');
    const checkoutVariantInput = section.querySelector('#mp-checkout-form [name="id"]');
    const priceEl = section.querySelector('.mp-price-sale');
    const comparePriceEl = section.querySelector('.mp-price-compare');
    const badgeEl = section.querySelector('.mp-price-badge');
    const atcBtn = form.querySelector('.enj-add-to-cart-btn');
    const atcBtnText = atcBtn ? atcBtn.querySelector('.mp-atc-label') : null;

    function getSelectedOptions() {
      const opts = [];
      form.querySelectorAll('.mp-option-group').forEach(group => {
        const checked = group.querySelector('input[type="radio"]:checked');
        if (checked) opts.push(checked.value);
      });
      return opts;
    }

    function findVariant(options) {
      return productData.variants.find(v =>
        v.options.every((opt, i) => opt === options[i])
      ) || null;
    }

    function formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney && typeof window.money_format !== 'undefined') {
        return Shopify.formatMoney(cents, window.money_format);
      }
      return '$' + (cents / 100).toFixed(2);
    }

    function updateUI(variant) {
      if (!variant) {
        if (atcBtn) {
          atcBtn.disabled = true;
          if (atcBtnText) atcBtnText.textContent = window.inventory_text?.unavailable || 'Unavailable';
        }
        return;
      }

      if (variantInput) variantInput.value = variant.id;
      if (checkoutVariantInput) checkoutVariantInput.value = variant.id;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url.toString());

      if (priceEl) priceEl.innerHTML = formatMoney(variant.price);
      if (comparePriceEl) {
        if (variant.compare_at_price > variant.price) {
          comparePriceEl.innerHTML = formatMoney(variant.compare_at_price);
          comparePriceEl.hidden = false;
          if (badgeEl) {
            const pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
            badgeEl.textContent = '-' + pct + '%';
            badgeEl.hidden = false;
          }
        } else {
          comparePriceEl.hidden = true;
          if (badgeEl) badgeEl.hidden = true;
        }
      }

      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        if (atcBtnText) {
          atcBtnText.textContent = variant.available
            ? (window.inventory_text?.add_to_cart || 'Add to Cart')
            : (window.inventory_text?.sold_out || 'Sold Out');
        }
      }

      if (variant.featured_image) {
        const carousel = section.querySelector('.main-product__carousel');
        if (carousel) {
          const slides = Array.from(carousel.querySelectorAll('.main-product__carousel-slide'));
          const matchIdx = slides.findIndex(slide => {
            const img = slide.querySelector('img');
            return img && img.dataset.mediaId === String(variant.featured_image.id);
          });
          if (matchIdx >= 0) {
            const slide = slides[matchIdx];
            const media = section.querySelector('.main-product__media');
            const currentLayout = media ? media.dataset.layout : '';
            const isMobile = window.innerWidth <= 767;
            if (!isMobile && (currentLayout === 'stacked' || currentLayout === '2col')) {
              slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            } else {
              carousel.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
            }
          }
        }
      }
    }

    form.querySelectorAll('.mp-option-group input[type="radio"]').forEach(radio => {
      radio.addEventListener('change', () => {
        const options = getSelectedOptions();
        const variant = findVariant(options);
        updateUI(variant);
      });
    });

    const initialOptions = getSelectedOptions();
    const initialVariant = findVariant(initialOptions);
    if (initialVariant) updateUI(initialVariant);
  }

  /* ---- Quantity Stepper ---- */

  function initQuantityStepper(section) {
    section.querySelectorAll('.mp-qty-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const wrapper = btn.closest('.mp-quantity-wrapper, .mp-inline-qty');
        if (!wrapper) return;

        const input = wrapper.querySelector('.mp-qty-input');
        if (!input) return;

        const current = parseInt(input.value, 10) || 1;
        const action = btn.dataset.qty;

        if (action === 'minus') {
          input.value = Math.max(1, current - 1);
        } else if (action === 'plus') {
          input.value = Math.min(999, current + 1);
        }

        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    section.querySelectorAll('.mp-qty-input').forEach(input => {
      input.addEventListener('change', () => {
        const val = parseInt(input.value, 10) || 1;
        input.value = Math.max(1, Math.min(999, val));
      });
    });
  }

  /* ---- Sticky ATC ---- */

  function initStickyAtc(section) {
    const stickyBar = document.querySelector('[data-sticky-atc]');
    if (!stickyBar) return;

    const stickyBtn   = stickyBar.querySelector('[data-sticky-atc-btn]');
    const variantLabel = stickyBar.querySelector('[data-sticky-atc-variant]');
    const mainAtcBtn  = section.querySelector('.mp-block-buy-buttons .enj-add-to-cart-btn');
    const footer      = document.querySelector('footer, [data-section-type="footer"], .site-footer, #shopify-section-footer');

    if (!mainAtcBtn) return;

    /* Sync variant label + button state from main form */
    function syncStickyState() {
      if (!stickyBtn) return;

      /* disabled state */
      stickyBtn.disabled = mainAtcBtn.disabled;

      /* label text */
      const labelEl = mainAtcBtn.querySelector('.mp-atc-label');
      const stickyLabelEl = stickyBtn.querySelector('.mp-atc-label');
      if (labelEl && stickyLabelEl) {
        stickyLabelEl.textContent = labelEl.textContent;
      }

      /* variant label */
      if (variantLabel) {
        const variantInput = section.querySelector('.main-product__form [name="id"]');
        if (variantInput) {
          const productData = JSON.parse(section.querySelector('[data-product-json]')?.textContent || 'null');
          if (productData) {
            const variant = productData.variants.find(v => String(v.id) === String(variantInput.value));
            if (variant && variant.title !== 'Default Title') {
              variantLabel.textContent = variant.title;
              variantLabel.hidden = false;
            } else {
              variantLabel.hidden = true;
            }
          }
        }
      }
    }

    /* Wire sticky button to submit the main form */
    if (stickyBtn) {
      stickyBtn.addEventListener('click', () => {
        const mainForm = section.querySelector('.main-product__form');
        if (mainForm) {
          /* Trigger the same submit so theme cart handlers fire */
          const submitBtn = mainForm.querySelector('.enj-add-to-cart-btn');
          if (submitBtn) {
            submitBtn.click();
          } else {
            mainForm.requestSubmit();
          }
        }
      });
    }

    /* Observe variant changes to keep sticky in sync */
    section.querySelectorAll('.mp-option-group input[type="radio"]').forEach(radio => {
      radio.addEventListener('change', () => {
        /* Slight delay so main JS updates the button first */
        setTimeout(syncStickyState, 50);
      });
    });

    /* Visibility logic via scroll */
    function isElementVisible(el) {
      const rect = el.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    }

    function updateStickyVisibility() {
      const atcVisible    = isElementVisible(mainAtcBtn);
      const footerVisible = footer ? isElementVisible(footer) : false;

      if (!atcVisible && !footerVisible) {
        stickyBar.setAttribute('data-visible', '');
        stickyBar.removeAttribute('aria-hidden');
      } else {
        stickyBar.removeAttribute('data-visible');
        stickyBar.setAttribute('aria-hidden', 'true');
      }
    }

    window.addEventListener('scroll', updateStickyVisibility, { passive: true });
    window.addEventListener('resize', updateStickyVisibility, { passive: true });

    syncStickyState();
    updateStickyVisibility();
  }

  /* ---- Init per section ---- */

  function initSection(sectionEl) {
    initCarousel(sectionEl);
    initThumbArrows(sectionEl);
    initVariantPicker(sectionEl);
    initQuantityStepper(sectionEl);
    initStickyAtc(sectionEl);
  }

  document.querySelectorAll('[data-section-type="main-product"]').forEach(initSection);

  document.addEventListener('shopify:section:load', (e) => {
    const sectionEl = e.target.querySelector('[data-section-type="main-product"]');
    if (sectionEl) initSection(sectionEl);
  });
})();
